import { useReadFile } from "@/queries/environment-queries";
import { cn } from "@/components/podkit/lib/cn";
import { Button } from "@/components/flexkit/Button";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import type React from "react";
import { type FC, useMemo, useCallback, useEffect, useRef } from "react";
import type { CachedCodeLine } from "@/components/ai/review/types";

export type CodeLine = {
    lineNumber: number;
    content: string;
    isHighlighted: boolean;
};

type CodeContextPreviewProps = {
    environmentId: string;
    filePath: string;
    startLine: number;
    endLine: number;
    /** Number of context lines to show before and after the selected range */
    contextLines?: number;
    /** Hash of the content when the comment was created */
    originalContentHash?: string;
    /** Callback when content hash and lines are computed (for storing on new comments) */
    onCodeContextComputed?: (hash: string, lines: CachedCodeLine[]) => void;
    /** Cached code lines to display when environment is offline */
    cachedCodeLines?: CachedCodeLine[];
    /** Show full file path instead of just filename */
    showFullPath?: boolean;
    /** Element to render at the start of the header (e.g., checkbox) */
    leadingElement?: React.ReactNode;
    /** Element to render at the end of the header (e.g., action buttons) */
    children?: React.ReactNode;
};

export const CodeContextPreview: FC<CodeContextPreviewProps> = ({
    environmentId,
    filePath,
    startLine,
    endLine,
    contextLines = 3,
    originalContentHash,
    onCodeContextComputed,
    cachedCodeLines,
    showFullPath = false,
    leadingElement,
    children,
}) => {
    const { data, isLoading, isError, error, refetch, isFetching } = useReadFile(environmentId, {
        path: filePath,
        enabled: !!environmentId && !!filePath,
    });

    const { lines: fetchedLines, contentHash } = useMemo(() => {
        if (!data?.result.case || data.result.case !== "content") {
            return { lines: null, contentHash: null };
        }

        const content = new TextDecoder().decode(data.result.value.data);
        const allLines = content.split("\n");

        // Calculate the range with context
        const contextStart = Math.max(1, startLine - contextLines);
        const contextEnd = Math.min(allLines.length, endLine + contextLines);

        const displayLines = allLines.slice(contextStart - 1, contextEnd).map((line, index) => ({
            lineNumber: contextStart + index,
            content: line,
            isHighlighted: contextStart + index >= startLine && contextStart + index <= endLine,
        }));

        return {
            lines: displayLines,
            contentHash: data.result.value.contentHash,
        };
    }, [data, startLine, endLine, contextLines]);

    // Use fetched lines if available, otherwise fall back to cached lines
    const lines = fetchedLines || cachedCodeLines || null;

    // Store callback in ref to avoid re-running effect when callback reference changes
    const onCodeContextComputedRef = useRef(onCodeContextComputed);
    onCodeContextComputedRef.current = onCodeContextComputed;

    // Report hash and lines for new comments (only when hash is first computed)
    useEffect(() => {
        if (contentHash && fetchedLines && !originalContentHash && onCodeContextComputedRef.current) {
            onCodeContextComputedRef.current(contentHash, fetchedLines);
        }
    }, [contentHash, fetchedLines, originalContentHash]);

    const isContentOutdated = useMemo(() => {
        if (!originalContentHash || !contentHash) return false;
        return originalContentHash !== contentHash;
    }, [originalContentHash, contentHash]);

    const handleRefresh = useCallback(() => {
        void refetch();
    }, [refetch]);

    const displayPath = showFullPath ? filePath : (filePath.split("/").pop() || filePath);
    const lineRange = startLine === endLine ? `L${startLine}` : `L${startLine}-${endLine}`;

    // Show loading only if we don't have cached lines to display
    if (isLoading && !cachedCodeLines) {
        return (
            <div className="overflow-hidden rounded-md border border-border-subtle bg-surface-primary">
                <div className="flex items-center justify-between border-b border-border-subtle bg-surface-secondary px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                        {leadingElement}
                        <span className="truncate font-mono text-xs text-content-secondary">{displayPath}</span>
                        <span className="shrink-0 text-xs text-content-muted">{lineRange}</span>
                    </div>
                    {children}
                </div>
                <div className="p-3">
                    <div className="h-4 w-32 animate-pulse rounded bg-surface-secondary" />
                    <div className="mt-2 h-4 w-48 animate-pulse rounded bg-surface-secondary" />
                    <div className="mt-2 h-4 w-40 animate-pulse rounded bg-surface-secondary" />
                </div>
            </div>
        );
    }

    // Show error state when API fails and no cached data available
    if (isError && !cachedCodeLines) {
        const errorMessage = error instanceof Error ? error.message : "Failed to load file content";
        const isEnvironmentOffline = errorMessage.includes("environment") || errorMessage.includes("offline") || errorMessage.includes("connect");
        
        return (
            <div className="overflow-hidden rounded-md border border-border-subtle bg-surface-primary">
                <div className="flex items-center justify-between border-b border-border-subtle bg-surface-secondary px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                        {leadingElement}
                        <span className="truncate font-mono text-xs text-content-secondary">{displayPath}</span>
                        <span className="shrink-0 text-xs text-content-muted">{lineRange}</span>
                    </div>
                    {children}
                </div>
                <div className="flex flex-col items-center gap-2 p-4 text-center">
                    <IconWarningCircle size="base" className="text-content-warning" />
                    <span className="text-sm text-content-secondary">
                        {isEnvironmentOffline 
                            ? "Environment is offline. Code preview unavailable."
                            : "Could not load code preview"}
                    </span>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isFetching}
                        LeadingIcon={IconRefresh}
                        data-tracking-id="retry-load-code-context"
                    >
                        {isFetching ? "Retrying..." : "Retry"}
                    </Button>
                </div>
            </div>
        );
    }

    if (!lines) {
        // No data and no error - shouldn't happen but handle gracefully
        return (
            <div className="overflow-hidden rounded-md border border-border-subtle bg-surface-primary">
                <div className="flex items-center justify-between border-b border-border-subtle bg-surface-secondary px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                        {leadingElement}
                        <span className="truncate font-mono text-xs text-content-secondary">{displayPath}</span>
                        <span className="shrink-0 text-xs text-content-muted">{lineRange}</span>
                    </div>
                    {children}
                </div>
            </div>
        );
    }

    const isUsingCachedData = !fetchedLines && !!cachedCodeLines;

    return (
        <div className="overflow-hidden rounded-md border border-border-subtle bg-surface-primary">
            {/* File annotation header */}
            <div className="flex items-center justify-between border-b border-border-subtle bg-surface-secondary px-3 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                    {leadingElement}
                    <span className="truncate font-mono text-xs text-content-secondary">{displayPath}</span>
                    <span className="shrink-0 text-xs text-content-muted">{lineRange}</span>
                </div>
                {children}
            </div>
            {/* Status warning: outdated content, using cached data, or refreshing */}
            {(isContentOutdated || isFetching || isUsingCachedData) && (
                <div className={cn(
                    "flex items-center justify-between border-b border-border-subtle px-3 py-1.5",
                    isUsingCachedData && !isContentOutdated ? "bg-surface-tertiary" : "bg-surface-warning-subtle"
                )}>
                    <span className={cn(
                        "text-xs",
                        isUsingCachedData && !isContentOutdated ? "text-content-secondary" : "text-content-warning"
                    )}>
                        {isFetching
                            ? "Refreshing..."
                            : isUsingCachedData
                              ? "Showing cached content (environment offline)"
                              : "Content has changed since this comment was created"}
                    </span>
                    {!isUsingCachedData && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={isFetching}
                            className="h-6 px-2"
                            data-tracking-id="refresh"
                        >
                            <IconRefresh size="sm" className={isFetching ? "animate-spin" : ""} />
                        </Button>
                    )}
                </div>
            )}
            <pre
                className="overflow-x-auto py-2 font-mono text-sm"
                aria-label={`Code from ${displayPath}, ${lineRange}`}
            >
                <code>
                    {lines.map(({ lineNumber, content, isHighlighted }) => (
                        <div key={lineNumber} className={cn("flex", isHighlighted ? "bg-surface-accent" : "")}>
                            <span
                                className="w-10 shrink-0 select-none px-2 text-right text-content-muted"
                                aria-hidden="true"
                            >
                                {lineNumber}
                            </span>
                            <span className={cn("flex-1 whitespace-pre px-2", isHighlighted ? "text-content-primary" : "text-content-secondary")}>
                                {content || " "}
                            </span>
                        </div>
                    ))}
                </code>
            </pre>
        </div>
    );
};
